import React, { useState, useEffect, useCallback } from 'react';
import { Minus, Square, Copy, X, Maximize2, Minimize2, BookOpenCheck } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Detects whether the app runs inside a desktop shell (Electron/Tauri)
 * that exposes native window methods. When present, the title bar
 * controls call the real OS APIs; otherwise they degrade to the best
 * behavior a browser tab can offer.
 */
const desktopShell = () => {
  if (typeof window === 'undefined') return false;
  return !!(window.minimize || window.maximize || window.unmaximize ||
    window.desktopAPI || window.ledgerproDesktop || window.electronAPI);
};

/**
 * Thin Windows-style application title bar. The controls map to real
 * window behaviors:
 *  - Minimize  → native `window.minimize()` in a desktop shell; otherwise
 *                a notice (browsers cannot minimize a tab).
 *  - Maximize  → native maximize/restore in a desktop shell; in a browser
 *                this is the Fullscreen API (the only real "fill screen").
 *  - Fullscreen→ dedicated Fullscreen API toggle, always available.
 *  - Close     → `window.close()`; if the browser blocks it, ends the
 *                session and returns to the login screen.
 * When the app is installed as a PWA with Window Controls Overlay, the
 * OS draws the native buttons and our custom controls are hidden.
 */
export default function ApplicationTitleBar({ companyName = 'LedgerPro', username = '' }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [osControls, setOsControls] = useState(false);
  const shell = desktopShell();

  // Keep button state in sync with actual fullscreen/maximize changes
  useEffect(() => {
    const sync = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!shell) setIsMaximized(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [shell]);

  // If the PWA is installed with Window Controls Overlay, the OS title
  // bar handles minimize/maximize/close — hide our custom buttons.
  useEffect(() => {
    const wco = navigator.windowControlsOverlay;
    if (!wco) return;
    const update = () => setOsControls(wco.visible);
    update();
    wco.addEventListener('geometrychange', update);
    return () => wco.removeEventListener('geometrychange', update);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch (err) {
      toast.error('Fullscreen is not available in this browser');
    }
  }, []);

  const doMinimize = () => {
    try {
      if (window.minimize) return window.minimize();
      if (window.desktopAPI?.minimize) return window.desktopAPI.minimize();
    } catch { /* fall through */ }
    toast('Minimize is available when running as a desktop app', { duration: 3000 });
  };

  const doMaximize = async () => {
    try {
      if (window.maximize && window.unmaximize) {
        if (isMaximized) { window.unmaximize(); setIsMaximized(false); }
        else { window.maximize(); setIsMaximized(true); }
        return;
      }
      if (window.desktopAPI?.maximize) {
        if (isMaximized) { window.desktopAPI.unmaximize(); setIsMaximized(false); }
        else { window.desktopAPI.maximize(); setIsMaximized(true); }
        return;
      }
    } catch { /* fall through */ }
    // Browser: the only real "fill the screen" is the Fullscreen API.
    await toggleFullscreen();
  };

  const doClose = () => {
    try {
      if (window.closeDesktopApp) return window.closeDesktopApp();
      if (window.desktopAPI?.close) return window.desktopAPI.close();
    } catch { /* fall through */ }
    try {
      window.close();
    } catch { /* ignored */ }
    // window.close() is silently blocked for tabs not opened by script.
    // If we're still here a moment later, end the session instead.
    setTimeout(() => {
      if (document.hidden) return; // window actually closed
      localStorage.removeItem('ledgerpro_token');
      localStorage.removeItem('ledgerpro_user');
      window.location.href = '/login';
    }, 350);
  };

  if (osControls) {
    // PWA Window Controls Overlay: OS draws the buttons; leave room for them.
    return (
      <div className="desk-titlebar" style={{ paddingInlineEnd: 138 }}>
        <div className="desk-titlebar-brand">
          <span className="desk-titlebar-logo"><BookOpenCheck size={10} /></span>
          <span>LedgerPro</span>
        </div>
        <div className="desk-titlebar-sep" />
        <div className="desk-titlebar-title">{companyName} — Accounting &amp; Business Management System</div>
      </div>
    );
  }

  return (
    <div className="desk-titlebar">
      <div className="desk-titlebar-brand">
        <span className="desk-titlebar-logo"><BookOpenCheck size={10} /></span>
        <span>LedgerPro</span>
      </div>
      <div className="desk-titlebar-sep" />
      <div className="desk-titlebar-title">
        {companyName} — Accounting &amp; Business Management System
      </div>
      <div className="desk-titlebar-controls">
        <button className="desk-win-btn" title="Minimize" aria-label="Minimize" onClick={doMinimize}>
          <Minus size={13} />
        </button>
        <button
          className={`desk-win-btn ${isMaximized ? 'active' : ''}`}
          title={isMaximized ? 'Restore' : 'Maximize'}
          aria-label={isMaximized ? 'Restore' : 'Maximize'}
          aria-pressed={isMaximized}
          onClick={doMaximize}
        >
          {isMaximized ? <Copy size={11} /> : <Square size={11} />}
        </button>
        <button
          className={`desk-win-btn ${isFullscreen ? 'active' : ''}`}
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          aria-pressed={isFullscreen}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
        </button>
        <button className="desk-win-btn desk-win-btn-close" title="Close" aria-label="Close" onClick={doClose}>
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
